import {
  IS_LOGGED_IN,
  IS_LOGGED_OUT,
  SET_ACCOUNT,
  SET_CURRENT_ACCOUNT,
  SET_TOKEN,
  SET_THEME,
  SET_SETTINGS,
  SET_WHITELABEL_OVERRIDE,
  CLEAR_ALL,
  UPDATE_CHAT_BADGE,
  SET_LOCKED,
  SET_SIGNED_IN_AT,
  SET_PASSKEY_PROMPT,
  SET_OAUTH_IN_PROGRESS,
  UPDATE_USER,
  NEW_VERSION,
  REMEMBER_ME,
  UPDATE_ACCOUNT_FEATURES,
  UPDATE_ACCOUNT_CHAT_CREDITS,
  BUMP_AVATAR_REFRESH,
  SET_NAV_PERSONAS,
  SET_LAST_PLAN,
} from '../constants';
export type ThemePreference = 'system' | 'light' | 'dark';

// Light until someone turns on Dark Mode or Match system in Settings.
export const getInitialTheme = (): ThemePreference => 'light';

interface SessionState {
  account: any;
  authorized: boolean;
  token: string | null;
  theme: ThemePreference;
  unread_chats: number;
  settings: any;
  locked: { show: boolean; inactive: boolean };
  /** When this session last came from a real sign-in, not a restore. */
  signed_in_at: number | null;
  /**
   * Wall-clock timestamp (ms) of the last successful unlock. Used by
   * restoreSession to skip the boot-time re-lock when the user just
   * unlocked seconds ago — refreshing within the grace window
   * shouldn't force them through the lock screen again. Persisted
   * across reloads (the session transform only strips `locked`, not
   * other fields), so a refresh within the grace window is recognised
   * even after a full page reload.
   */
  unlocked_at: number | null;
  /**
   * Post-registration "set up a passkey" offer (ED-2799). Set once by
   * confirmSignUp, cleared when the sheet closes; never re-raised for a user
   * who already has a passkey.
   */
  passkeyPrompt: { show: boolean; source: string } | null;
  oauthInProgress: boolean;
  newVersionAvailable: boolean;
  last_logged_in: any;
  logging_out: boolean;
  remember_me: boolean;
  avatarTimestamps: Record<string, number>;
  navigationPersonas: string[];
  /**
   * The plan each signed-in person last had open, keyed by
   * `${account_id}:${cognito_id}` (lastPlanScope). With no plans list to land
   * on, the app opens this plan; persisted with the rest of the session.
   */
  lastPlanByScope: Record<string, number>;
  /**
   * Pre-auth white-label branding resolved from the host the app is served
   * on (`<subdomain>.eleplan.com` → partner branding via the public
   * /partners/by-subdomain lookup). Lets the login + register screens paint
   * the partner's logo and colors before the user signs in. Yields to a
   * real authed partner/account branding once available (see useTheme /
   * BrandLogo precedence), so it's a pre-auth fallback, not an override of
   * the signed-in user's own context.
   */
  whitelabelOverride: WhitelabelOverride | null;
}

export interface WhitelabelOverride {
  company_name?: string | null;
  brand_primary?: string | null;
  brand_secondary?: string | null;
  brand_logo?: string | null;
  level?: number | null;
}

const initialState: SessionState = {
  account: false,
  authorized: false,
  token: null,
  theme: getInitialTheme(),
  unread_chats: 0,
  settings: false,
  locked: { show: false, inactive: false },
  unlocked_at: null,
  signed_in_at: null,
  passkeyPrompt: null,
  oauthInProgress: false,
  newVersionAvailable: false,
  last_logged_in: false,
  logging_out: false,
  remember_me: true,
  avatarTimestamps: {},
  navigationPersonas: [],
  lastPlanByScope: {},
  whitelabelOverride: null,
};

const sessionReducer = (state = initialState, { type, payload }: { type: string; payload?: any }): SessionState => {
  switch (type) {
    case IS_LOGGED_IN:
      return { ...state, authorized: true };
    case IS_LOGGED_OUT:
      return { ...state, authorized: false, logging_out: false };
    case SET_ACCOUNT:
    case SET_CURRENT_ACCOUNT:
      return { ...state, account: payload };
    case SET_TOKEN:
      return { ...state, token: payload };
    case SET_THEME:
      return { ...state, theme: payload as ThemePreference };
    case SET_WHITELABEL_OVERRIDE:
      return { ...state, whitelabelOverride: payload || null };
    case SET_SETTINGS:
      const hasWatchCompanionFlag = Object.prototype.hasOwnProperty.call(payload || {}, 'watch_companion_enabled');
      const hasCarPlayFlag = Object.prototype.hasOwnProperty.call(payload || {}, 'carplay_enabled');
      const existingSettings = (state.settings && typeof state.settings === 'object') ? state.settings : {};
      return {
        ...state,
        settings: {
          ...existingSettings,
          ...(payload || {}),
          watch_companion_enabled: hasWatchCompanionFlag
            ? payload?.watch_companion_enabled === true
            : existingSettings.watch_companion_enabled === true,
          carplay_enabled: hasCarPlayFlag
            ? payload?.carplay_enabled === true
            : existingSettings.carplay_enabled === true,
          fetched: new Date().toISOString(),
        },
      };
    case UPDATE_CHAT_BADGE:
      return { ...state, unread_chats: payload };
    case SET_SIGNED_IN_AT:
      return { ...state, signed_in_at: Date.now() };
    case SET_LOCKED:
      // Stamp `unlocked_at` whenever we transition into the unlocked
      // state (show === false). restoreSession reads this to apply a
      // short grace window before re-imposing the lock on refresh —
      // refreshing 5s after unlock shouldn't re-prompt.
      return {
        ...state,
        locked: { ...payload },
        unlocked_at: payload?.show === false ? Date.now() : state.unlocked_at,
      };
    case SET_PASSKEY_PROMPT:
      return { ...state, passkeyPrompt: payload && payload.show ? { show: true, source: String(payload.source || 'register') } : null };
    case SET_OAUTH_IN_PROGRESS:
      return { ...state, oauthInProgress: !!payload };
    case UPDATE_USER:
      return {
        ...state,
        last_logged_in: {
          email: payload.email,
          avatar: payload.avatar,
          cognito_id: payload.cognito_id,
          first_name: payload.first_name,
          last_name: payload.last_name,
        },
      };
    case NEW_VERSION:
      return { ...state, newVersionAvailable: true };
    case REMEMBER_ME:
      return {
        ...state,
        remember_me: payload,
        // Respect privacy expectations: disabling remember-me removes saved last user.
        last_logged_in: payload ? state.last_logged_in : false,
      };
    case UPDATE_ACCOUNT_FEATURES:
      return {
        ...state,
        account: state.account ? { ...state.account, features: { ...(state.account.features || {}), ...payload } } : state.account,
      };
    case UPDATE_ACCOUNT_CHAT_CREDITS:
      // Only for the account it was measured on: a turn that ends after a
      // switch must not paint another account's allowance.
      return state.account && payload?.account_id === state.account.account_id
        ? { ...state, account: { ...state.account, chat_credits: payload.credits } }
        : state;
    case BUMP_AVATAR_REFRESH:
      return {
        ...state,
        avatarTimestamps: { ...state.avatarTimestamps, [payload]: Date.now() },
      };
    case SET_NAV_PERSONAS:
      return { ...state, navigationPersonas: Array.isArray(payload) ? payload : [] };
    case SET_LAST_PLAN: {
      const projectId = Number(payload?.projectId);
      if (!payload?.scope || !Number.isSafeInteger(projectId) || projectId <= 0) return state;
      if (state.lastPlanByScope?.[payload.scope] === projectId) return state;
      return { ...state, lastPlanByScope: { ...(state.lastPlanByScope || {}), [payload.scope]: projectId } };
    }
    case CLEAR_ALL:
      return {
        ...initialState,
        theme: state.theme,
        last_logged_in: state.last_logged_in,
        // The host hasn't changed on sign-out, so keep the partner branding
        // so the login screen stays branded after the user logs out.
        whitelabelOverride: state.whitelabelOverride,
      };
    default:
      return state;
  }
};

export default sessionReducer;
